import { useState, type FormEvent } from 'react';
import { saveCustomFieldDefinition } from '@/modules/inventory/services';
import { ActiveBadge } from '@/modules/inventory/components';
import { useCategories, useCustomFields } from '@/modules/inventory/hooks';
import {
  CUSTOM_FIELD_TYPE_LABELS,
  OPTION_FIELD_TYPES,
  type CustomFieldDefinition,
  type CustomFieldDefinitionInput,
  type CustomFieldType,
} from '@/modules/inventory/types';
import {
  formatOptionsForInput,
  parseOptionsText,
  toFieldKey,
} from '@/modules/inventory/utils';
import { useAuth } from '@/shared/hooks/useAuth';

const empty: CustomFieldDefinitionInput = {
  categoryId: '',
  subcategoryId: null,
  key: '',
  label: '',
  type: 'text',
  options: [],
  required: false,
  sortOrder: 0,
  active: true,
};

export function CustomFieldsPage() {
  const { categories } = useCategories();
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const { fields, loading, error, refresh } = useCustomFields(
    filterCategoryId || undefined,
  );
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('catalog.write');

  const [form, setForm] = useState<CustomFieldDefinitionInput>(empty);
  const [optionsText, setOptionsText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const showOptions = OPTION_FIELD_TYPES.includes(form.type);

  function startEdit(field: CustomFieldDefinition) {
    setEditingId(field.id);
    setForm({
      categoryId: field.categoryId,
      subcategoryId: field.subcategoryId,
      key: field.key,
      label: field.label,
      type: field.type,
      options: field.options,
      required: field.required,
      sortOrder: field.sortOrder,
      active: field.active,
    });
    setOptionsText(formatOptionsForInput(field.options));
  }

  function resetForm() {
    setEditingId(null);
    setForm({ ...empty, categoryId: filterCategoryId || '' });
    setOptionsText('');
    setFormError(null);
  }

  function handleLabelChange(label: string) {
    setForm((prev) => ({
      ...prev,
      label,
      key: editingId ? prev.key : toFieldKey(label),
    }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite || !form.categoryId || !form.label.trim() || !form.key.trim()) {
      setFormError('Categoría, etiqueta y clave son obligatorios.');
      return;
    }
    if (showOptions && parseOptionsText(optionsText).length === 0) {
      setFormError('Agregá al menos una opción (una por línea).');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await saveCustomFieldDefinition(
        {
          ...form,
          options: showOptions ? parseOptionsText(optionsText) : [],
          subcategoryId: form.subcategoryId || null,
        },
        editingId ?? undefined,
      );
      resetForm();
      await refresh();
    } catch {
      setFormError('No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <h1>Campos personalizados</h1>
      <p className="auth-muted">
        Definí atributos por categoría. Los artículos los completan al crearse.
      </p>

      <label className="auth-label">
        Filtrar por categoría
        <select
          value={filterCategoryId}
          onChange={(e) => setFilterCategoryId(e.target.value)}
        >
          <option value="">Todas</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      {canWrite ? (
        <form className="inventory-panel" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Editar campo' : 'Nuevo campo'}</h2>
          <div className="users-form-grid">
            <label className="auth-label">
              Categoría *
              <select
                required
                value={form.categoryId}
                onChange={(e) =>
                  setForm({ ...form, categoryId: e.target.value, subcategoryId: null })
                }
              >
                <option value="">—</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="auth-label">
              Etiqueta *
              <input
                required
                value={form.label}
                onChange={(e) => handleLabelChange(e.target.value)}
              />
            </label>
            <label className="auth-label">
              Clave (slug) *
              <input
                required
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
              />
            </label>
            <label className="auth-label">
              Tipo
              <select
                value={form.type}
                onChange={(e) =>
                  setForm({ ...form, type: e.target.value as CustomFieldType })
                }
              >
                {(Object.keys(CUSTOM_FIELD_TYPE_LABELS) as CustomFieldType[]).map(
                  (t) => (
                    <option key={t} value={t}>
                      {CUSTOM_FIELD_TYPE_LABELS[t]}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label className="auth-label">
              Orden
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) =>
                  setForm({ ...form, sortOrder: Number(e.target.value) || 0 })
                }
              />
            </label>
          </div>
          {showOptions ? (
            <label className="auth-label">
              Opciones (una por línea)
              <textarea
                rows={4}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
              />
            </label>
          ) : null}
          <label className="auth-label auth-label--row">
            <input
              type="checkbox"
              checked={form.required}
              onChange={(e) => setForm({ ...form, required: e.target.checked })}
            />
            Obligatorio
          </label>
          <label className="auth-label auth-label--row">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Activo
          </label>
          {formError ? <p className="auth-error">{formError}</p> : null}
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              Guardar
            </button>
            {editingId ? (
              <button
                type="button"
                className="auth-button auth-button--secondary"
                onClick={resetForm}
              >
                Cancelar
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      <h2>Definiciones</h2>
      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}
      <table className="users-table">
        <thead>
          <tr>
            <th>Etiqueta</th>
            <th>Clave</th>
            <th>Tipo</th>
            <th>Estado</th>
            {canWrite ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.id}>
              <td>{f.label}</td>
              <td>
                <code>{f.key}</code>
              </td>
              <td>{CUSTOM_FIELD_TYPE_LABELS[f.type]}</td>
              <td>
                <ActiveBadge active={f.active} />
              </td>
              {canWrite ? (
                <td>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => startEdit(f)}
                  >
                    Editar
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
